import { useState, useMemo } from "react";
import { UtensilsCrossed, Receipt, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useOpenConsumption, OpenConsumption } from "@/hooks/useOpenConsumption";
import { OpenConsumptionCard } from "@/components/open-consumption/OpenConsumptionCard";
import { OpenConsumptionDetailsDialog } from "@/components/open-consumption/OpenConsumptionDetailsDialog";
import { CloseConsumptionDialog } from "@/components/open-consumption/CloseConsumptionDialog";
import { TransferItemDialog } from "@/components/open-consumption/TransferItemDialog";
import { CancelOrphanDialog } from "@/components/open-consumption/CancelOrphanDialog";

export default function ConsumoAbertoPage() {
  const { openTables, openCommands, isLoading, refetch, cancelOrphanOrders } = useOpenConsumption();
  const [selectedConsumption, setSelectedConsumption] = useState<OpenConsumption | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Combine all open consumptions for transfer destinations
  const allOpenConsumptions = useMemo(() => {
    return [...openTables, ...openCommands];
  }, [openTables, openCommands]);

  const handleViewDetails = (item: OpenConsumption) => {
    setSelectedConsumption(item);
    setDetailsOpen(true);
  };

  const handleCloseConsumption = (item: OpenConsumption) => {
    setSelectedConsumption(item);
    setCloseDialogOpen(true);
  };

  const handleTransfer = (item: OpenConsumption) => {
    setSelectedConsumption(item);
    setTransferDialogOpen(true);
  };

  const handleCancelOrphan = (item: OpenConsumption) => {
    setSelectedConsumption(item);
    setCancelDialogOpen(true);
  };

  const confirmCancelOrphan = async () => {
    if (!selectedConsumption) return;
    setIsCancelling(true);
    try {
      await cancelOrphanOrders(selectedConsumption);
      setCancelDialogOpen(false);
      setSelectedConsumption(null);
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Receipt className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Consumo Aberto</h1>
            <p className="text-sm text-muted-foreground">
              Visualize mesas e comandas com pedidos em aberto
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={refetch}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Tabs defaultValue="tables" className="space-y-4">
          <TabsList>
            <TabsTrigger value="tables" className="gap-2">
              <UtensilsCrossed className="h-4 w-4" />
              Mesas ({openTables.length})
            </TabsTrigger>
            <TabsTrigger value="commands" className="gap-2">
              <Receipt className="h-4 w-4" />
              Comandas ({openCommands.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tables" className="space-y-4">
            {openTables.length === 0 ? (
              <div className="text-center py-12">
                <UtensilsCrossed className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhuma mesa ocupada</h3>
                <p className="text-muted-foreground">
                  As mesas com consumo aparecerão aqui
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {openTables.map((item) => (
                  <OpenConsumptionCard
                    key={item.id}
                    type="table"
                    number={item.number}
                    customerName={item.customerName}
                    items={item.items}
                    subtotal={item.subtotal}
                    openedAt={item.openedAt}
                    ordersCount={item.ordersCount}
                    isStale={item.isStale}
                    onViewDetails={() => handleViewDetails(item)}
                    onClose={() => handleCloseConsumption(item)}
                    onTransfer={allOpenConsumptions.length > 1 ? () => handleTransfer(item) : undefined}
                    onCancel={item.isStale ? () => handleCancelOrphan(item) : undefined}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="commands" className="space-y-4">
            {openCommands.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhuma comanda em uso</h3>
                <p className="text-muted-foreground">
                  As comandas com consumo aparecerão aqui
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {openCommands.map((item) => (
                  <OpenConsumptionCard
                    key={item.id}
                    type="command"
                    number={item.number}
                    customerName={item.customerName}
                    items={item.items}
                    subtotal={item.subtotal}
                    openedAt={item.openedAt}
                    ordersCount={item.ordersCount}
                    isStale={item.isStale}
                    onViewDetails={() => handleViewDetails(item)}
                    onClose={() => handleCloseConsumption(item)}
                    onTransfer={allOpenConsumptions.length > 1 ? () => handleTransfer(item) : undefined}
                    onCancel={item.isStale ? () => handleCancelOrphan(item) : undefined}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      <OpenConsumptionDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        consumption={selectedConsumption}
      />

      <CloseConsumptionDialog
        open={closeDialogOpen}
        onOpenChange={setCloseDialogOpen}
        consumption={selectedConsumption}
        onSuccess={() => {
          setCloseDialogOpen(false);
          refetch();
        }}
      />

      <TransferItemDialog
        open={transferDialogOpen}
        onOpenChange={setTransferDialogOpen}
        sourceConsumption={selectedConsumption}
        destinations={allOpenConsumptions}
        onSuccess={() => {
          refetch();
        }}
      />

      <CancelOrphanDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        consumption={selectedConsumption}
        onConfirm={confirmCancelOrphan}
        isLoading={isCancelling}
      />
    </div>
  );
}
